import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { message } from 'antd';
import { API_BASE_URL, REQUEST_TIMEOUT, getApiPrefix } from './config';
import type { ApiResponse } from '../types';
import type { Supplier } from '../types/supplier';

function createApiClient(supplier: Supplier) {
  return axios.create({
    baseURL: `${API_BASE_URL}${getApiPrefix(supplier)}`,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

function attachInterceptors(client: ReturnType<typeof createApiClient>) {
  client.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      const data = response.data;
      if (data && data.result !== undefined) {
        return { ...response, data: data.result };
      }
      return response;
    },
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      if (error.response) {
        const status = error.response.status;
        switch (status) {
          case 401:
            message.error('未授权，请重新登录');
            break;
          case 403:
            message.error('拒绝访问');
            break;
          case 404:
            message.error('请求的资源不存在');
            break;
          case 500:
            message.error('服务器内部错误');
            break;
          default:
            message.error(`请求错误: ${error.message}`);
        }
      } else if (error.request) {
        if (error.code !== 'ERR_CANCELED') {
          message.error('网络错误，请检查网络连接');
        }
      } else {
        message.error(`请求错误: ${error.message}`);
      }

      return Promise.reject(error);
    },
  );
}

const apiClients: Record<Supplier, ReturnType<typeof createApiClient>> = {
  giga: createApiClient('giga'),
  aosom: createApiClient('aosom'),
};

attachInterceptors(apiClients.giga);
attachInterceptors(apiClients.aosom);

export function getApiClient(supplier: Supplier = 'giga') {
  return apiClients[supplier];
}

export async function jsonRpcRequest<T>(
  path: string,
  params: Record<string, unknown> | unknown = {},
  supplier: Supplier = 'giga',
): Promise<ApiResponse<T>> {
  try {
    const response = await getApiClient(supplier).post(path, {
      jsonrpc: '2.0',
      method: 'call',
      params: params,
      id: Date.now(),
    });

    return response.data as ApiResponse<T>;
  } catch (error) {
    console.error('API 请求失败:', error);
    throw error;
  }
}

export async function restRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  data?: Record<string, unknown>,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<T>> {
  try {
    const response = await getApiClient(supplier).request<ApiResponse<T>>({
      method,
      url: path,
      data: method !== 'GET' ? data : undefined,
      params: method === 'GET' ? data : undefined,
    });

    return response.data;
  } catch (error) {
    console.error('API 请求失败:', error);
    throw error;
  }
}

export default getApiClient('giga');
